import React, { useState, useEffect } from 'react';
import { Search, Plus, ChevronDown, Calendar, Download } from "lucide-react";
import api from '../../../services/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../../store/authStore';

export const CustomersControlPanel = ({
  search, setSearch,
  filters, setFilters,
  viewStyle, setViewStyle,
  onAddClick,
  stages = []
}) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(!!search);
  const { user } = useAuthStore();
  const [discountLimit, setDiscountLimit] = useState(25);
  const [isAdminOrOwner, setIsAdminOrOwner] = useState(false);

  useEffect(() => {
    const userRole = user?.role?.toLowerCase() || '';
    setIsAdminOrOwner(userRole === 'admin' || userRole === 'owner');

    if (userRole === 'admin' || userRole === 'owner') {
      const fetchCompanySettings = async () => {
        try {
          const res = await api.get('/company-settings');
          const data = res.data?.data || res.data || {};
          if (data.discountLimit !== undefined) {
            setDiscountLimit(data.discountLimit);
          }
        } catch (err) { }
      };
      fetchCompanySettings();
    }
  }, [user]);

  const handleSaveDiscountLimit = async (newLimit) => {
    try {
      await api.put('/company-settings', { discountLimit: Number(newLimit) });
      setDiscountLimit(Number(newLimit));
      toast.success("Discount limit updated");
    } catch (err) {
      toast.error("Failed to update discount limit");
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 w-full bg-transparent pb-2 overflow-x-auto custom-scrollbar">
      {/* Left Group */}
      <div className="flex items-center gap-2">
        {/* Search Icon Button and Input */}
        <div className="flex items-center gap-2 relative">
          <button
            type="button"
            onClick={() => setIsSearchExpanded(!isSearchExpanded)}
            className={`flex items-center justify-center w-9 h-9 border rounded-lg transition-colors shrink-0 shadow-sm ${isSearchExpanded ? 'bg-slate-100 border-gray-300 text-slate-800' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
          >
            <Search size={16} />
          </button>

          {isSearchExpanded && (
            <input
              type="text"
              value={search || ""}
              onChange={(e) => setSearch && setSearch(e.target.value)}
              placeholder="Search name, SD ID, phone..."
              className="h-9 px-3 border border-gray-200 rounded-lg bg-white text-gray-700 text-[13px] font-medium focus:outline-none focus:border-slate-400 shadow-sm shrink-0 w-48 transition-all"
              autoFocus
            />
          )}
        </div>

        {/* Contact Stages Dropdown */}
        <select
          value={filters?.status || 'all'}
          onChange={(e) => setFilters && setFilters(prev => ({ ...prev, status: e.target.value }))}
          className="h-9 px-3 border border-gray-200 rounded-lg bg-white text-gray-700 text-[13px] font-medium focus:outline-none focus:border-slate-400 cursor-pointer shadow-sm shrink-0"
        >
          <option value="all">All Contact Stages</option>
          <option value="new">New</option>
          <option value="estimation_shared">Estimation Shared</option>
          <option value="order_confirmed">Order Confirmed</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="in_store">In Store</option>
          <option value="dispatched">Dispatched</option>
          <option value="completed">Completed</option>
        </select>

        {/* Date Picker: Exact */}
        <div className="flex items-center gap-1.5 h-9 px-3 border border-gray-200 rounded-lg bg-white text-gray-700 text-[13px] font-medium shadow-sm shrink-0 relative">
          <Calendar size={14} className="text-gray-400" />
          <span className="text-gray-400 text-[11px] font-semibold select-none">Date:</span>
          <input
            type="date"
            value={filters?.exactDate || ""}
            onChange={(e) => setFilters && setFilters({ exactDate: e.target.value, dateRange: null })}
            className="outline-none border-none text-[12px] bg-white text-gray-700 font-semibold cursor-pointer w-28"
          />
          {filters?.exactDate && (
            <button
              type="button"
              onClick={() => setFilters && setFilters({ exactDate: "" })}
              className="text-gray-400 hover:text-red-500 font-bold ml-1 text-xs"
              title="Clear"
            >
              ×
            </button>
          )}
        </div>

        {/* T W M Y Buttons Group */}
        <div className="flex items-center gap-1 shrink-0 ml-1">
          {['T', 'W', 'M', 'Y'].map(label => {
            const isActive = filters?.dateRange === label;
            return (
              <button
                key={label}
                type="button"
                onClick={() => {
                  if (setFilters) {
                    const newRange = isActive ? null : label;
                    setFilters({ dateRange: newRange, exactDate: "" });
                  }
                }}
                className={`w-9 h-9 flex items-center justify-center border rounded-lg text-[12px] font-bold transition-all shadow-sm ${isActive ? 'bg-[#0f172a] text-white border-[#0f172a]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              >
                {label}
              </button>
            );
          })}
        </div>


      </div>

      {/* Right Group */}
      <div className="flex items-center gap-2 ml-auto pl-4 shrink-0">

        {isAdminOrOwner && (
          <div className="flex items-center gap-2 mr-2 border-r pr-4 border-slate-200">
            <label className="text-[11px] font-bold text-slate-500 whitespace-nowrap">Discount Limit To:</label>
            <input
              type="number"
              className="w-12 h-8 px-1 text-xs text-center border border-slate-300 rounded focus:border-[#C91D25] outline-none"
              value={discountLimit}
              onChange={(e) => setDiscountLimit(e.target.value)}
            />
            <span className="text-[11px] font-bold text-slate-500">%</span>
            <button
              onClick={() => handleSaveDiscountLimit(discountLimit)}
              className="text-[11px] font-bold text-white bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded ml-1 tracking-wider"
            >
              Save
            </button>
          </div>
        )}

        {/* Add Contact Button */}
        <button
          onClick={onAddClick}
          className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#0F172A] text-white text-[13px] font-medium hover:bg-[#1E293B] transition-colors shadow-sm cursor-pointer"
        >
          <Plus size={14} className="text-white" />
          <span>Add Customer</span>
        </button>
      </div>
    </div>
  );
};